// Field "My activity forms" — submitted activity-form entries. Backed by
// GET /activity-submissions, which is already visibility-scoped
// (backend/routes_activity_forms.py list_submissions via VisibilityResolver):
// an SE sees only their own submissions; an ASM sees their own PLUS their
// team's, for free — no separate "manager view" endpoint needed.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ClipboardList, CheckCircle2, XCircle, Clock, MapPin } from "lucide-react";
import { api, API_BASE, getToken, formatApiError, photoRefPath } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const STATUS_BADGE = {
  submitted: "bg-amber-100 text-amber-800 border-amber-200",
  verified:  "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected:  "bg-rose-100 text-rose-800 border-rose-200",
};
const STATUS_ICON = { submitted: Clock, verified: CheckCircle2, rejected: XCircle };

// GET /api/photos/{ref} needs a bearer token a plain <img src> can't send —
// fetch the bytes ourselves and hand the <img>/link a local object URL
// (same pattern as RetailerDetailsSheet.jsx / TadaApproval.jsx).
function PhotoThumb({ ref: photoRef, label }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!photoRef) return;
    let revoked = null;
    fetch(`${API_BASE}/photos/${photoRefPath(photoRef)}`,
      { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
      .then((blob) => { const u = URL.createObjectURL(blob); revoked = u; setUrl(u); })
      .catch(() => setFailed(true));
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [photoRef]);

  if (!photoRef) return null;
  return (
    <div>
      {label && <div className="text-[10px] uppercase tracking-wide text-neutral-400 mb-1">{label}</div>}
      {failed ? (
        <div className="h-32 border border-dashed rounded-lg flex items-center justify-center text-[11px] text-neutral-400">
          Couldn't load
        </div>
      ) : url ? (
        <img src={url} alt={label || "photo"} className="w-full h-32 object-cover rounded-lg border border-neutral-200" />
      ) : (
        <div className="h-32 rounded-lg bg-neutral-100 animate-pulse" />
      )}
    </div>
  );
}

function SubmissionDetailDialog({ submissionId, onClose }) {
  const [sub, setSub] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setSub(null); setErr(null);
    api.get(`/activity-submissions/${submissionId}`)
      .then(({ data }) => setSub(data))
      .catch((e) => setErr(formatApiError(e)));
  }, [submissionId]);

  const StatusIcon = sub ? (STATUS_ICON[sub.status] || Clock) : Clock;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="submission-detail-dialog">
        {!sub && !err && (
          <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-neutral-400" /></div>
        )}
        {err && <div className="text-sm text-red-600 py-6 text-center">{err}</div>}
        {sub && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {sub.form_name_snapshot}
                <Badge className={"border text-[10px] flex items-center gap-1 " + (STATUS_BADGE[sub.status] || "")}>
                  <StatusIcon className="h-3 w-3" /> {sub.status}
                </Badge>
              </DialogTitle>
              <DialogDescription>
                {sub.retailer?.name || "—"} · {sub.user?.name || "—"} · {new Date(sub.submission_ts).toLocaleString()}
              </DialogDescription>
            </DialogHeader>

            {sub.gps && (
              <div className="text-xs bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-emerald-800 flex items-center gap-2">
                <MapPin className="w-3 h-3" />
                GPS: {Number(sub.gps.lat).toFixed(5)}, {Number(sub.gps.lng).toFixed(5)}
              </div>
            )}

            <div className="space-y-3">
              {(sub.fields || []).filter(f => f.field_type !== "section_divider").map((f) => {
                const v = (sub.values || {})[f.field_key];
                if (f.field_type === "photo_pair") {
                  const pair = v || {};
                  if (!pair.before && !pair.after) return null;
                  return (
                    <div key={f.id}>
                      <div className="text-xs text-neutral-500 mb-1">{f.label}</div>
                      <div className="grid grid-cols-2 gap-2">
                        <PhotoThumb ref={pair.before} label="Before" />
                        <PhotoThumb ref={pair.after} label="After" />
                      </div>
                    </div>
                  );
                }
                if (f.field_type === "photo") {
                  if (!v) return null;
                  return (
                    <div key={f.id}>
                      <div className="text-xs text-neutral-500 mb-1">{f.label}</div>
                      <PhotoThumb ref={v} />
                    </div>
                  );
                }
                if (f.field_type === "signature") {
                  if (!v) return null;
                  return (
                    <div key={f.id}>
                      <div className="text-xs text-neutral-500 mb-1">{f.label}</div>
                      <PhotoThumb ref={v} />
                    </div>
                  );
                }
                if (v == null || v === "") return null;
                return (
                  <div key={f.id} className="flex items-start justify-between gap-3 text-sm border-b border-neutral-100 pb-2">
                    <span className="text-neutral-500">{f.label}</span>
                    <span className="text-right font-medium text-neutral-900">
                      {typeof v === "object" ? JSON.stringify(v) : String(v)}
                    </span>
                  </div>
                );
              })}
            </div>

            {sub.compliance_score != null && (
              <div className="text-sm font-semibold text-neutral-900">Compliance score: {sub.compliance_score}</div>
            )}
            {sub.reviewer_notes && (
              <div className="text-xs text-neutral-500 italic">Reviewer note: "{sub.reviewer_notes}"</div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function MyActivitySubmissions() {
  const { user } = useAuth();
  const isASM = user?.role_code === "ASM";
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get("/activity-submissions", { params: { page_size: 50 } })
      .then(({ data }) => { setItems(data.items || []); setTotal(data.total ?? 0); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  return (
    <div className="p-4 pb-32 space-y-4" data-testid="my-activity-submissions-page">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-neutral-400 flex items-center gap-1.5">
          <ClipboardList className="h-3.5 w-3.5" /> Activity forms
        </div>
        <h1 className="text-2xl font-extrabold text-neutral-900">
          {isASM ? "My & team's submissions" : "My submissions"}
        </h1>
        <p className="text-xs text-neutral-500 mt-0.5">{total} submitted so far.</p>
      </div>

      {loading ? (
        <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-neutral-400" /></div>
      ) : !items || items.length === 0 ? (
        <div className="py-10 text-center text-neutral-400 text-sm">No forms submitted yet.</div>
      ) : (
        <div className="space-y-2">
          {items.map((s) => {
            const StatusIcon = STATUS_ICON[s.status] || Clock;
            return (
              <Card key={s.id} className="border-neutral-200 rounded-2xl cursor-pointer active:bg-neutral-50"
                    onClick={() => setOpenId(s.id)}
                    data-testid={`my-sub-row-${s.id.slice(0, 8)}`}>
                <CardContent className="p-3 space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-neutral-900 truncate">{s.form_name_snapshot}</div>
                      <div className="text-[11px] text-neutral-400">{fmtDate(s.submission_ts)}</div>
                    </div>
                    <Badge className={"border text-[10px] shrink-0 flex items-center gap-1 " + (STATUS_BADGE[s.status] || "")}>
                      <StatusIcon className="h-3 w-3" /> {s.status}
                    </Badge>
                  </div>
                  {isASM && s.user_name && (
                    <div className="text-[11px] text-neutral-500">By {s.user_name}</div>
                  )}
                  {s.retailer_name && (
                    <div className="text-[11px] text-neutral-500">
                      {s.retailer_code} · {s.retailer_name}
                    </div>
                  )}
                  {s.compliance_score != null && (
                    <div className="text-[11px] text-neutral-700 font-semibold">Score: {s.compliance_score}</div>
                  )}
                  {s.reviewer_notes && (
                    <div className="text-[11px] text-neutral-500 italic">"{s.reviewer_notes}"</div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {openId && (
        <SubmissionDetailDialog submissionId={openId} onClose={() => setOpenId(null)} />
      )}
    </div>
  );
}
